import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import type { Group } from '../domain/group';
import type { GroupConfigEnterprise } from '../domain/groupConfig';
import type { Invitation } from '../domain/invitation';

import type { Fleet } from '@/fleet/domain/fleet';
import { defaultReservationConfig, type ReservationClass, type ReservationClassProps } from '@/subscription/domain/resaClass';

// TODO break down in different files and see how to manage it in store.ts

export interface CompanySlice {
    pendingGroupConfigEnterprise: {
        __component: 'user.enterprise';
        name?: string;
        color?: string;
        logo?: string;
        memberSyncSource?: string;
        superAdminsGroup?: Group;
        superFleet?: Fleet;
        address?: {
            address: string;
            city: string;
            zip: string;
            country: string;
        };
        email?: string;
        phone?: string;
        contact?: {
            firstname: string;
            surname: string;
            email: string;
            phone: string;
            fax: string;
        }
    },
    pendingResaClass: ReservationClassProps,
    companyAdminGroup: {
        assignedFleets: [],
        defaultDuration: 0,
        groupConfigs: [],
        invitations: [],
        members: [],
        name: '',
        packets: [],
        prefix: '',
        id?: -1;
    },
    group: {
        defaultDuration: 0,
        groupConfig: {
            groupConfig: [],
            userGroup: {
                name: '',
            }
        },
        members: [],
        name: '',
        packets: [],
        prefix: ''
    };
    remainingSlots: number;
}
export interface PendingCompanySlice {
    companyAdminGroup: Group;
    pendingGroupConfigEnterprise: GroupConfigEnterprise,
    group: Group,
    pendingResaClass: ReservationClassProps | ReservationClass;
    remainingSlots: number;
}
const initialState: PendingCompanySlice = {
    group: {
        defaultDuration: 0,
        groupConfigs: [],
        id: -1,
        members: [],
        name: '',
        packet: {
            isOverflowFixed: false,
            resaClassRef: '',
            reservedNumber: 0,
        },
        prefix: ''
    },
    pendingGroupConfigEnterprise: {
        __component: 'user.enterprise',
        address: {
            address: '',
            city: '',
            country: '',
            zip: ''
        },
        contact: {
            email: '',
            fax: '',
            firstname: '',
            phone: '',
            surname: ''
        },
        id: 0,
        logo: '',
        name: '',
    },
    pendingResaClass: {
        config: { ...defaultReservationConfig },
        slotNumber: 0,
    },
    remainingSlots: 0
};

const companySlice = createSlice({
    initialState,
    name: 'company',
    reducers: {
        clearCompanyAdminGroup(state) {
            state.companyAdminGroup = initialState.companyAdminGroup;
            return state;
        },

        clearPendingGroupConfigEnterprise: (state) => {
            state.pendingGroupConfigEnterprise = initialState.pendingGroupConfigEnterprise;
            return state;
        },

        clearPendingResaClass: (state) => {
            return { ...state, pendingResaClass: {
                config: { ...defaultReservationConfig },
                slotNumber: 0,
            } };
        },
        setRemainingSlots(state, action: PayloadAction<number>) {
            state.remainingSlots = action.payload;
        },
        removeInvitationFromCompanyAdminGroup(state, action: PayloadAction<number>) {
            const invitations = (state.companyAdminGroup.invitations as Invitation[])?.slice();
            invitations?.splice(action.payload, 1); // TODO replace by find with id?
            state.companyAdminGroup.invitations = invitations;
            return state;
        },
        setCompanyAdminGroup(state, action: PayloadAction<Group>) {
            state.companyAdminGroup = action.payload;
            return state;
        },
        setCompanyGroup(state, action) {
            return { ...state, group: action.payload };
        },
        setPendingGroupConfigEnterprise: (state, action: PayloadAction<GroupConfigEnterprise>) => {
            state.pendingGroupConfigEnterprise = action.payload;
            return state;
        },
        setPendingResaClass: (
            state,
            action: PayloadAction<ReservationClassProps>
        ) => {
            // Deep-clone so the pending copy doesn't share nested references
            // (config/features/notifications) with currResaClass — otherwise a
            // toggle can mutate shared state and the UI desyncs from the flag.
            state.pendingResaClass = structuredClone(action.payload);
            return state;
        },
        updatePendingGroupConfigEnterpriseProps(state, action: PayloadAction<{ field: string, value: string }>) {
            const { field, value } = action.payload;

            const nState = { ...state };

            // Check if superGroupConfig exists
            if (!state.pendingGroupConfigEnterprise) {
                return state; // Return current state if superGroupConfig doesn't exist
            }

            // Split field into nested parts
            const fieldParts = field.split('.');

            // If field is nested
            if (fieldParts.length > 1) {
                const topLevelField = fieldParts[0];
                const nestedFields = fieldParts.slice(1); // Remove the first part (top-level field)

                /*
                 * Check if top-level field exists 
                 * TODO : either set all possible fields in initialState or find another way to check ot topLevel field makes sense
                 */
                if (typeof (initialState.pendingGroupConfigEnterprise[topLevelField]) === 'undefined') {
                    return state; // Return current state if top-level field doesn't exist
                }

                // Create a new object with updated nested fields
                const updatedNestedFields = {
                    ...state.pendingGroupConfigEnterprise[topLevelField],
                    [nestedFields.join('.')]: value // Update the nested field
                };

                // Create a new superGroupConfig object with the updated nested fields
                const updatedPendingGroupConfigEnterprise = {
                    ...state.pendingGroupConfigEnterprise,
                    [topLevelField]: updatedNestedFields // Update the top-level field
                };

                // Return the new state with updated superGroupConfig
                state.pendingGroupConfigEnterprise = updatedPendingGroupConfigEnterprise;

                return state;
            } else {

                // If field is not nested, update it directly
                nState.pendingGroupConfigEnterprise = {
                    ...state.pendingGroupConfigEnterprise,
                    [field]: value // Update the field
                };


                return nState;
            }
        },

        updatePendingResaClassProps(state, action: PayloadAction<{ field: string, value: unknown }>) {
            const { field, value } = action.payload;

            // Check if pendingResaClass exists
            if (!state.pendingResaClass) {
                return state; // Return current state if pendingResaClass doesn't exist
            }

            // Split field into nested parts
            const fieldParts = field.split('.');

            // For nested paths, guard that the top-level key is known before writing.
            if (fieldParts.length > 1 && typeof (initialState.pendingResaClass[fieldParts[0]]) === 'undefined') {
                return state; // Return current state if top-level field doesn't exist
            }

            // Deep-set the (possibly multi-level, e.g. config.features.enableReservation)
            // path directly on the Immer draft so nested config sub-components are stored
            // as real nested objects.
            let target = state.pendingResaClass as Record<string, any>;
            for (let i = 0; i < fieldParts.length - 1; i++) {
                const key = fieldParts[i];
                if (target[key] == null || typeof target[key] !== 'object') {
                    target[key] = {};
                }
                target = target[key];
            }
            target[fieldParts.at(-1)!] = value;

            return state;
        },

    },
});

export const { clearPendingResaClass, clearCompanyAdminGroup, setCompanyAdminGroup,
    setCompanyGroup, setPendingResaClass, updatePendingResaClassProps,
    removeInvitationFromCompanyAdminGroup,
    setPendingGroupConfigEnterprise, clearPendingGroupConfigEnterprise, updatePendingGroupConfigEnterpriseProps,
    setRemainingSlots
} = companySlice.actions;
export default companySlice.reducer;